// Boot: owns the window, the fixed-timestep loop, and the screenshot harness
// contract (window.__TF). Everything else lives behind `screens/`.

import { FIXED_DT, MAX_STEPS_PER_FRAME } from './core/constants.js';

const canvas = document.getElementById('scene');
const uiRoot = document.getElementById('ui');

const params = new URLSearchParams(location.search);
const shotMode = params.get('shot') === '1';

/** Harness contract — see docs/CONTRACTS.md §11. */
const TF = (window.__TF = {
  ready: false,
  error: null,
  state: null,
  game: null,
  params,
  step: null,
  screenshotReady: null,
});

function fatal(message, detail) {
  TF.error = message + (detail ? ' — ' + detail : '');
  uiRoot.innerHTML = `
    <div class="fatal">
      <div class="fatal-card">
        <h1>Train Fight can't start</h1>
        <p>${escapeHtml(message)}</p>
        ${detail ? `<pre>${escapeHtml(String(detail))}</pre>` : ''}
        <p class="fatal-hint">Try the latest Chrome, Edge or Firefox on a machine with
        hardware graphics enabled.</p>
      </div>
    </div>`;
  TF.ready = true; // let the harness screenshot the error card rather than hang
}

function escapeHtml(s) {
  return String(s).replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c]);
}

async function boot() {
  let createRenderer, createGame, initAudio, initInput;
  try {
    ({ createRenderer } = await import('./render/index.js'));
    ({ createGame } = await import('./screens/index.js'));
    ({ initAudio } = await import('./audio/index.js'));
    ({ initInput } = await import('./input/index.js'));
  } catch (e) {
    return fatal('Failed to load game modules.', e.stack || e.message);
  }

  // Explicit canvas size in shot mode keeps screenshots pixel-stable.
  const forcedW = Number(params.get('w')) || 0;
  const forcedH = Number(params.get('h')) || 0;

  let renderer;
  try {
    renderer = createRenderer(canvas, {
      width: forcedW || window.innerWidth,
      height: forcedH || window.innerHeight,
      deterministic: shotMode,
      pixelRatio: shotMode ? 2 : Math.min(window.devicePixelRatio || 1, 2),
    });
  } catch (e) {
    if (e && e.name === 'NoWebGLError') {
      return fatal("This browser couldn't start WebGL.", e.message);
    }
    return fatal('The 3D renderer failed to start.', e.stack || e.message);
  }

  const audio = shotMode ? null : initAudio();
  const input = initInput({ enabled: !shotMode });

  let game;
  try {
    game = createGame({ canvas, uiRoot, renderer, audio, input, params, shotMode });
  } catch (e) {
    return fatal('The game failed to initialise.', e.stack || e.message);
  }
  TF.game = game;

  if (!shotMode) {
    const onResize = () => {
      game.resize(window.innerWidth, window.innerHeight);
    };
    window.addEventListener('resize', onResize);
    onResize();
    installFullscreenButton();
  } else {
    game.resize(forcedW || 1600, forcedH || 900);
  }

  // ---- Deterministic (screenshot) path ------------------------------------
  if (shotMode) {
    try {
      game.gotoScreen(params.get('scene') || 'battle', paramsToOptions(params));
      const t = Number(params.get('t') || 0);
      const steps = Math.max(0, Math.round(t / FIXED_DT));
      for (let i = 0; i < steps; i++) game.step(FIXED_DT);
      TF.state = game.getState ? game.getState() : null;
      game.render(0, FIXED_DT);
      // A second render pass lets any lazily-compiled shader / first-frame
      // effect settle before the screenshot is taken.
      game.render(0, FIXED_DT);
    } catch (e) {
      return fatal('Deterministic render failed.', e.stack || e.message);
    }
    TF.step = (n) => {
      for (let i = 0; i < n; i++) game.step(FIXED_DT);
      game.render(0, FIXED_DT);
    };
    TF.ready = true;
    return;
  }

  // ---- Live path ----------------------------------------------------------
  // ?debug=teleport arms the watchdog for the reported "trains jump between
  // tracks" bug. Four static investigations found nothing, so this gathers
  // evidence from an actual play session instead of guessing again.
  let teleportTick = null;
  if (params.get('debug') === 'teleport') {
    try {
      const { installTeleportWatch } = await import('./core/teleportWatch.js');
      teleportTick = installTeleportWatch(() => (game.getState ? game.getState() : null));
    } catch (e) {
      console.error('[main] teleport watch failed to load', e);
    }
  }

  game.gotoScreen(params.get('scene') || 'title', paramsToOptions(params));

  let last = performance.now() / 1000;
  let accumulator = 0;

  function frame(nowMs) {
    const now = nowMs / 1000;
    let dtReal = now - last;
    last = now;
    if (dtReal > 0.25) dtReal = 0.25; // tab was backgrounded

    accumulator += dtReal;
    let steps = 0;
    while (accumulator >= FIXED_DT && steps < MAX_STEPS_PER_FRAME) {
      game.step(FIXED_DT);
      accumulator -= FIXED_DT;
      steps++;
    }
    if (steps === MAX_STEPS_PER_FRAME) accumulator = 0; // give up catching up

    game.render(accumulator / FIXED_DT, dtReal);
    TF.state = game.getState ? game.getState() : null;
    if (teleportTick) teleportTick(dtReal);
    requestAnimationFrame(frame);
  }

  TF.ready = true;
  requestAnimationFrame(frame);
}

/**
 * Fullscreen toggle.
 *
 * Deliberately built here rather than in `screens/`: the screen state machine
 * tears down and rebuilds `#ui` on every transition, so a control that must
 * survive title -> select -> battle belongs outside it. Styles are inline for the
 * same reason — `styles.css` is the screens module's, and this button is not.
 *
 * Hidden while actually fullscreen (the whole point is to get out of the way),
 * and reachable from the keyboard as well as the mouse. `F` is safe to bind: no
 * player control uses it.
 */
function installFullscreenButton() {
  const btn = document.createElement('button');
  btn.type = 'button';
  btn.id = 'tf-fullscreen';
  btn.setAttribute('aria-label', 'Toggle fullscreen');
  btn.title = 'Fullscreen (F)';
  btn.style.cssText = [
    'position:fixed',
    'right:14px',
    'bottom:14px',
    'z-index:40',
    'width:44px',
    'height:44px',
    'display:grid',
    'place-items:center',
    'padding:0',
    'cursor:pointer',
    'border-radius:9px',
    'border:2px solid rgba(255,255,255,.22)',
    'background:rgba(20,26,32,.72)',
    'color:#f2f3f1',
    'font:600 17px/1 ui-sans-serif,system-ui,sans-serif',
    'box-shadow:0 2px 0 rgba(0,0,0,.45),0 6px 18px rgba(0,0,0,.35)',
    'backdrop-filter:blur(6px)',
    '-webkit-backdrop-filter:blur(6px)',
    'transition:transform .12s ease,border-color .12s ease',
  ].join(';');

  const ENTER_ICON = '⛶';
  const EXIT_ICON = '⤫';
  btn.textContent = ENTER_ICON;

  const isFull = () => !!(document.fullscreenElement || document.webkitFullscreenElement);

  async function toggle() {
    try {
      if (isFull()) {
        await (document.exitFullscreen ? document.exitFullscreen() : document.webkitExitFullscreen());
      } else {
        const el = document.documentElement;
        await (el.requestFullscreen ? el.requestFullscreen({ navigationUI: 'hide' }) : el.webkitRequestFullscreen());
      }
    } catch (e) {
      // Safari and locked-down kiosk setups can refuse. Not worth a crash — the
      // game is perfectly playable windowed.
      console.error('[fullscreen] request refused', e);
    }
  }

  function sync() {
    const full = isFull();
    btn.textContent = full ? EXIT_ICON : ENTER_ICON;
    btn.title = full ? 'Leave fullscreen (F or Esc)' : 'Fullscreen (F)';
    btn.style.opacity = full ? '0' : '1';
    btn.style.pointerEvents = full ? 'none' : 'auto';
  }

  btn.addEventListener('click', toggle);
  btn.addEventListener('mouseenter', () => {
    btn.style.transform = 'translateY(-1px)';
    btn.style.borderColor = 'rgba(242,205,55,.85)';
  });
  btn.addEventListener('mouseleave', () => {
    btn.style.transform = 'none';
    btn.style.borderColor = 'rgba(255,255,255,.22)';
  });
  document.addEventListener('fullscreenchange', sync);
  document.addEventListener('webkitfullscreenchange', sync);
  window.addEventListener('keydown', (e) => {
    // Ignore the repeat while held, and never steal F from a text field.
    if (e.repeat || e.metaKey || e.ctrlKey || e.altKey) return;
    const t = e.target;
    if (t && (t.tagName === 'INPUT' || t.tagName === 'TEXTAREA' || t.isContentEditable)) return;
    if (e.code === 'KeyF') {
      e.preventDefault();
      toggle();
    }
  });

  (document.getElementById('app') || document.body).appendChild(btn);
  sync();
}

function paramsToOptions(p) {
  const list = (k) => (p.get(k) ? p.get(k).split(',').filter(Boolean) : null);
  return {
    track: p.get('track') || undefined,
    trains: list('trains') || undefined,
    seed: p.get('seed') != null ? Number(p.get('seed')) : 1,
    cam: p.get('cam') || undefined,
    camTarget: p.get('camTarget') || undefined,
    frame: p.get('frame') != null ? Number(p.get('frame')) : undefined,
    focus: p.get('focus') || undefined,
    event: p.get('event') || undefined,
    give: list('give') || undefined,
    force: list('force') || undefined,
    hud: p.get('hud') === '1',
    winner: p.get('winner') || undefined,
  };
}

boot().catch((e) => fatal('Unexpected start-up failure.', e.stack || e.message));
